import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Mail, CheckCircle2, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TITLE = "Customer Support | Apex AV Denver";
const DESC = "Existing Apex AV customers can request service for home theater, audio, networking, Control4, lighting, shades, and security systems in Denver.";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://apexav.com/support" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/support" }],
  }),
  component: SupportPage,
});

const CATEGORIES = ["Home theater", "Whole-home audio", "TV / video", "Network / Wi-Fi", "Control4 / automation", "Lighting", "Shades", "Security cameras", "Other"];

function SupportPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", category: "", system_brand: "",
    priority: "normal", preferred_contact: "Phone", issue: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.issue.trim().length < 5) return setError("Please describe the issue.");
    setSending(true);
    try {
      const res = await fetch("/api/support", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Something went wrong.");
      setDone(json.ticketNumber ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  const sel = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

  return (
    <section className="container mx-auto px-6 py-16 md:py-24">
      <div className="mx-auto grid max-w-5xl gap-12 md:grid-cols-[1fr_1.4fr]">
        <div>
          <LifeBuoy className="h-8 w-8 text-primary" />
          <h1 className="mt-4 font-display text-4xl font-bold md:text-5xl">Customer support</h1>
          <p className="mt-4 text-muted-foreground">
            Already an Apex AV client? Tell us what's going on and we'll get back to you, usually the same business day.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <a href="tel:+13033173233" className="flex items-center gap-2 hover:text-primary"><Phone className="h-4 w-4" /> (303) 317-3233</a>
            <a href="mailto:info@apexav.com" className="flex items-center gap-2 hover:text-primary"><Mail className="h-4 w-4" /> info@apexav.com</a>
          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            New project instead? <Link to="/contact" className="text-primary underline">Request a consultation</Link>.
          </p>
        </div>

        {done !== null ? (
          <div className="rounded-lg border border-border p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-4 font-display text-2xl font-bold">Request received</h2>
            {done ? <p className="mt-2 text-sm text-muted-foreground">Your ticket number is <strong className="text-foreground">#{done}</strong>.</p> : null}
            <p className="mt-2 text-muted-foreground">Thanks, {form.name.split(" ")[0]}. Our team will reach out soon. If it's urgent, call us at (303) 317-3233.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-lg border border-border p-6 md:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name *"><Input required maxLength={100} value={form.name} onChange={set("name")} autoComplete="name" /></Field>
              <Field label="Phone *"><Input required type="tel" maxLength={30} value={form.phone} onChange={set("phone")} autoComplete="tel" /></Field>
            </div>
            <Field label="Email *"><Input required type="email" maxLength={255} value={form.email} onChange={set("email")} autoComplete="email" /></Field>
            <Field label="Service address"><Input maxLength={300} value={form.address} onChange={set("address")} autoComplete="street-address" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="System">
                <select className={sel} value={form.category} onChange={set("category")}>
                  <option value="">Select…</option>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Brand / device (optional)"><Input maxLength={80} placeholder="e.g. Sonos, Control4" value={form.system_brand} onChange={set("system_brand")} /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="How urgent?">
                <select className={sel} value={form.priority} onChange={set("priority")}>
                  <option value="low">Low — whenever convenient</option>
                  <option value="normal">Normal</option>
                  <option value="high">High — affecting daily use</option>
                  <option value="urgent">Urgent — system down</option>
                </select>
              </Field>
              <Field label="Best way to reach you">
                <select className={sel} value={form.preferred_contact} onChange={set("preferred_contact")}>
                  <option>Phone</option><option>Text</option><option>Email</option>
                </select>
              </Field>
            </div>
            <Field label="Describe the issue *"><Textarea required rows={5} maxLength={4000} value={form.issue} onChange={set("issue")} placeholder="What's happening, which room, and when it started" /></Field>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={sending} className="w-full">{sending ? "Sending…" : "Submit support request"}</Button>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>;
}
